import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import { Skeleton, Stack, CircularProgress, Typography } from '@mui/material'
import { NodeRendererProps, Tree } from 'react-arborist'
import { FolderShared, Folder, FolderOpen, FolderDelete, FolderSpecial, Storage, Image } from '@mui/icons-material'

import { TreeNode, FullNodeProp } from '../../utils/types'
import '../../styles/css/tree.css'
import { isPrivate } from '../../utils/config'
import { fetchPost } from '../../utils/http'
import { bglightblue, muiblue } from '../../styles/js/colors.style'
import { DashContext } from '../common/context'


const CACHE_DURATION = 2 * 60 * 1000 // 2 minutes cache for status API

const fetchTooltipData = async (nodeData: any, signal: AbortSignal) => {
    const status = {prediction: 'Pending', userEdits: 'None'}

    try {
        const payload: any = { file_id: nodeData.file_id }
        if ('s3_name' in nodeData) {
            payload.s3_name = nodeData.s3_name
            payload.key = nodeData.full_path.slice(nodeData.s3_name.length).replace(/^\/|\/$/g, "")
        }
        const res = await fetchPost('Annotations/status', payload, signal)
        if (!res.ok) throw new Error('failed')
        const data = await res.json()
        
        if (data.status.toLowerCase() === 'failed') status.prediction = 'Failed'
        else if (data.status.toLowerCase() === 'processed') status.prediction = 'Completed'
        else if (data.status.toLowerCase() === 'processing') status.prediction = 'Running'

        if (data.user_markers) status.userEdits = 'Present'

        return status
    } catch {
        return status
    }
}

interface NodeExtraProps {
    hoverTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
    hideTimer: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
    abortRef: React.MutableRefObject<AbortController | null>
    setTooltip: React.Dispatch<React.SetStateAction<{
        id: string
        data: {prediction: string, userEdits: string} | null
    }>>
    toolTip: {id: string, data: {prediction: string, userEdits: string} | null}
    tooltipCache: React.MutableRefObject<Map<string, CacheEntry>>
    nodeIdSet: Set<string>
}

const Node = ({
    node,
    style,
    dragHandle,
    hoverTimer,
    hideTimer,
    abortRef,
    setTooltip,
    toolTip,
    tooltipCache,
    nodeIdSet
}: NodeRendererProps<any> & NodeExtraProps) => {

    const onMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!node.isLeaf) return

        if (hoverTimer.current) clearTimeout(hoverTimer.current)
        hoverTimer.current = setTimeout(async () => {
            // check cache first
            const cached = tooltipCache.current.get(node.data.id)
            const now = Date.now()
            if (cached && now - cached.timestamp < CACHE_DURATION) {
                setTooltip({id: node.data.id, data: cached.data})
                if (hideTimer.current) clearTimeout(hideTimer.current)
                hideTimer.current = setTimeout(() => setTooltip({id: '', data: null}), 5000)
                return
            }
            // abort prev request and send new
            abortRef.current?.abort()
            abortRef.current = new AbortController()
            setTooltip({id: node.data.id, data: null})  // loading...
            try {
                const data = await fetchTooltipData(node.data, abortRef.current.signal)
                tooltipCache.current.set(node.data.id, {data, timestamp: Date.now()})  // save to cache
                setTooltip({id: node.data.id, data})
                if (hideTimer.current) clearTimeout(hideTimer.current)
                hideTimer.current = setTimeout(() => setTooltip({id: '', data: null}), 5000)
            } catch {
                if (hideTimer.current) clearTimeout(hideTimer.current)
            }
        }, 1500)
    }

    const onMouseLeave = () => {
        if (hoverTimer.current) {
            clearTimeout(hoverTimer.current)
            hoverTimer.current = null
        }
        if (hideTimer.current) clearTimeout(hideTimer.current)
        abortRef.current?.abort()
        setTooltip(prevState => {
            if (prevState.id.length) return {id: '', data: null}
            else return prevState
        })
    }

    return (
        <div
            style={{
                ...style,
                background: node.isSelected ? bglightblue : "transparent",
            }}
            className='directory-node'
            ref={dragHandle}
            onClick={() => node.toggle()}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {node.isLeaf
                ? <Image sx={{color: 'grey'}} />
                : node.isOpen
                    ? node.data.is_loading
                        ? <CircularProgress size={18} color="primary" sx={{marginRight: '6px'}} />
                        : <FolderOpen color="primary" />
                    : node.data.shared
                        ? <FolderShared color="primary" />
                        : node.data.name === 'master-dataset'
                            ? <Storage color="primary" />
                            : node.data.name === 'Bin'
                                ? <FolderDelete color="primary" />
                                : node.data.name === 'Mounts'
                                    ? <FolderSpecial color="primary" />
                                    : <Folder color="primary" />
            }
            {toolTip.id===node.data.id
                ? <div
                    style={(node.level===1 && node.rowIndex===1) ? {top: '112%'} : {bottom: '65%'}}
                    className="alert alert-primary shadow tree-tooltip"
                    role="alert"
                >   
                    {toolTip.data
                        ? <>
                            <Typography variant="caption">Prediction: {toolTip.data?.prediction}</Typography>
                            <Typography variant="caption">User Edits: {toolTip.data?.userEdits}</Typography>
                        </>
                        : <CircularProgress size={14} />
                    }
                </div>
                : null
            }
            {/* '<userid>_' is for differentiating same folders/files from different users */}
            <span className="node-text" style={{ color: nodeIdSet.has(node.data.id) ? muiblue : 'unset' }}>
                {node.data.name.startsWith('<') && node.data.name.indexOf('>_') > 0
                    ? node.data.name.slice(node.data.name.indexOf('>_') + 2)
                    : node.data.name
                }
            </span>
        </div>
    )
}

interface RenderTreeProps {
    mainTree: TreeNode,
    mountedTree: TreeNode,
    sharedTree: TreeNode,
    binTree: TreeNode,
    s3Tree: TreeNode,
    width: number,
    height: number,
    selectNode: Function,
    focusNodeId: string,
    searchTerm: string
}

type CacheEntry = {
  data: any
  timestamp: number
}

const RenderTree: React.FC<RenderTreeProps> = ({
    mainTree, mountedTree, sharedTree, binTree, s3Tree, width, height,
    selectNode, focusNodeId, searchTerm
}) => {
    const { selectedNodes } = useContext(DashContext)

    const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const abortRef = useRef<AbortController | null>(null)
    const tooltipCache = useRef<Map<string, CacheEntry>>(new Map())

    const [tree, setTree] = useState<(FullNodeProp | null)[]>([])
    const [tooltip, setTooltip] = useState<{
        id: string
        data: {prediction: string, userEdits: string} | null
    }>({id: '', data: null})

    const rowHeight = 28
    const isDev = ['dev'].includes(localStorage.getItem('role') || 'null')


    const nodeIdSet = useMemo(() => new Set(selectedNodes.map(n => n && n.id)), [selectedNodes])

    // Tree order - My Folder, Shared, Mounts, Bin, S3

    useEffect(() => {
        if (isPrivate) {
            if (isDev) setTree([null, null, null, null ,null])
            setTree([null, null, null, null])
        } else {
            if (isDev) setTree([null, null, null])
            setTree([null, null])
        }
    }, [])  // eslint-disable-line

    useEffect(() => {
        if (!Object.keys(mainTree).length) return
        const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
        worker.postMessage(mainTree)
        worker.onmessage = (event) => {
            if (event.data[0] && 'id' in event.data[0]) {
                setTree(prevArray => {
                    const newArray = [...prevArray]
                    newArray[0] = event.data[0]
                    return newArray
                })
            }
        }
        worker.onerror = (error) => {
            console.error('Worker error:', error)
        }
        return () => {
            worker.terminate()
        }
    }, [mainTree])

    useEffect(() => {
        if (isPrivate) {
            if (!Object.keys(mountedTree).length) return
            const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
            worker.postMessage(mountedTree)
            worker.onmessage = (event) => {
                if (event.data[0] && 'id' in event.data[0]) {
                    setTree(prevArray => {
                        const newArray = [...prevArray]
                        newArray[1] = event.data[0]
                        return newArray
                    })
                }
            }
            worker.onerror = (error) => {
                console.error('Worker error:', error)
            }
            return () => {
                worker.terminate()
            }
        }
    }, [mountedTree])   // eslint-disable-line

    useEffect(() => {
        if (!Object.keys(sharedTree).length) return
        const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
        worker.postMessage(sharedTree)
        worker.onmessage = (event) => {
            if (event.data[0] && 'id' in event.data[0]) {
                setTree(prevArray => {
                    const newArray = [...prevArray]
                    if (isPrivate) newArray[2] = event.data[0]
                    else newArray[1] = event.data[0]
                    return newArray
                })
            }
        }
        worker.onerror = (error) => {
            console.error('Worker error:', error)
        }
        return () => {
            worker.terminate()
        }
    }, [sharedTree])    // eslint-disable-line

    useEffect(() => {
        if (isPrivate) {
            if (!Object.keys(s3Tree).length) return
            const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
            worker.postMessage(s3Tree)
            worker.onmessage = (event) => {
                if (event.data[0] && 'id' in event.data[0]) {
                    setTree(prevArray => {
                        const newArray = [...prevArray]
                        newArray[3] = event.data[0]
                        return newArray
                    })
                }
            }
            worker.onerror = (error) => {
                console.error('Worker error:', error)
            }
            return () => {
                worker.terminate()
            }
        }
    }, [s3Tree])    // eslint-disable-line

    useEffect(() => {
        if (isDev) {
            if (!Object.keys(binTree).length) return
            const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
            worker.postMessage(binTree)
            worker.onmessage = (event) => {
                if (event.data[0] && 'id' in event.data[0]) {
                    setTree(prevArray => {
                        const newArray = [...prevArray]
                        if (isPrivate) newArray[4] = event.data[0]
                        else newArray[2] = event.data[0]
                        return newArray
                    })
                }
            }
            worker.onerror = (error) => {
                console.error('Worker error:', error)
            }
            return () => {
                worker.terminate()
            }
        }
    }, [binTree])   // eslint-disable-line

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            const node = nodes[0]
            let key: string = ''
            // .isLeaf is true for file nodes
            if (node.isLeaf) key = node.data.full_path!.split(node.data.name)[0] + node.data.name
            else {
                key = node.data.full_path!.split(node.data.name)[0] + node.data.name + '/'
                if (key.startsWith('master-dataset/') && node.children && !node.children.length) node.data.is_loading = true
            }
            selectNode(key)
        }
    }
    
    const cleanupCache = () => {
        const now = Date.now()
        tooltipCache.current.forEach((entry, key) => {
            if (now - entry.timestamp > CACHE_DURATION) tooltipCache.current.delete(key)
        })
    }
    // periodic cleanup every 2m
    useEffect(() => {
        const interval = setInterval(cleanupCache, CACHE_DURATION)
        return () => clearInterval(interval)
    }, [])

    return (
        <>
        {tree.every(i => i!==null)
            ? <Tree
                data={tree}
                openByDefault={false}
                rowHeight={rowHeight}
                onSelect={onSelect}
                height={height}
                width={width}
                disableDrag={true}
                selection={focusNodeId}
                searchTerm={searchTerm}
            >
                {(props) => (
                    <Node
                        {...props}
                        hoverTimer={hoverTimer}
                        hideTimer={hideTimer}
                        abortRef={abortRef}
                        setTooltip={setTooltip}
                        toolTip={tooltip}
                        tooltipCache={tooltipCache}
                        nodeIdSet={nodeIdSet}
                    />
                )}
            </Tree>
            : <Stack spacing={1}>
                <Skeleton variant='rectangular' height={24} />
                <Skeleton variant='rectangular' height={24} />
                <Skeleton variant='rectangular' height={24} />
                {isDev && <Skeleton variant='rectangular' height={24} />}
                {isPrivate && <Skeleton variant='rectangular' height={24} />}
            </Stack>
        }
        </>
    )
    
}

export default RenderTree